import L from 'leaflet';

// Fix Leaflet marker icons in Vite
// We use the images from the leaflet package
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

const defaultIcon = L.icon({
    iconUrl: markerIcon,
    iconRetinaUrl: markerIcon2x,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = defaultIcon;

export class MapSelector {
    private map: L.Map;
    private marker: L.Marker | null = null;
    private circle: L.Circle | null = null;
    private searchTimeout: number | null = null;
    
    constructor(mapElementId: string) {
        // Initialize map centered on Amsterdam
        this.map = L.map(mapElementId).setView([52.3676, 4.9041], 13);
        
        // Add OpenStreetMap tiles
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap contributors',
            maxZoom: 19
        }).addTo(this.map);
        
        // Setup click handler
        this.map.on('click', (e: L.LeafletMouseEvent) => {
            this.placeMarker(e.latlng);
        });
    }
    
    async placeMarker(latlng: L.LatLng): Promise<void> {
        // Remove existing marker
        if (this.marker) {
            this.map.removeLayer(this.marker);
        }
        
        // Add new marker
        this.marker = L.marker(latlng).addTo(this.map);
        
        // Update circle
        this.updateCircle(latlng);
        
        // Reverse geocode to get location name
        const locationName = await this.reverseGeocode(latlng.lat, latlng.lng);
        
        // Update form fields
        this.updateFormFields(latlng.lat, latlng.lng, locationName);
        
        // Enable generate button
        const generateBtn = document.getElementById('btn-generate') as HTMLButtonElement;
        if (generateBtn) {
            generateBtn.disabled = false;
        }
    }
    
    updateCircle(latlng: L.LatLng): void {
        const radiusInput = document.getElementById('radius-slider') as HTMLInputElement;
        const radius = parseInt(radiusInput?.value || '50');
        
        if (this.circle) {
            this.map.removeLayer(this.circle);
        }
        
        this.circle = L.circle(latlng, {
            radius: radius,
            color: '#FF6600',
            fillColor: '#FF6600',
            fillOpacity: 0.2
        }).addTo(this.map);
    }
    
    async reverseGeocode(lat: number, lng: number): Promise<string> {
        try {
            const response = await fetch(`/api/geocode/reverse?latitude=${lat}&longitude=${lng}`);
            const data = await response.json();
            return data.display_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
        } catch (error) {
            console.error('Reverse geocoding failed:', error);
            return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
        }
    }
    
    updateFormFields(lat: number, lng: number, locationName: string): void {
        const locationInput = document.getElementById('location-name') as HTMLInputElement;
        const latSpan = document.getElementById('location-lat');
        const lngSpan = document.getElementById('location-lng');
        
        if (locationInput) locationInput.value = locationName;
        if (latSpan) latSpan.textContent = lat.toFixed(6);
        if (lngSpan) lngSpan.textContent = lng.toFixed(6);
    }
    
    async searchLocation(query: string): Promise<void> {
        if (query.length < 3) return;
        
        try {
            const response = await fetch(`/api/geocode?query=${encodeURIComponent(query)}`);
            const data = await response.json();
            this.displaySearchResults(data.results);
        } catch (error) {
            console.error('Geocoding search failed:', error);
        }
    }
    
    displaySearchResults(results: any[]): void {
        const resultsDiv = document.getElementById('geocode-results');
        if (!resultsDiv) return;
        
        if (results.length === 0) {
            resultsDiv.classList.remove('has-results');
            return;
        }
        
        // Clear previous results safely
        resultsDiv.innerHTML = '';
        
        // Build result items using safe DOM manipulation
        results.forEach(result => {
            const resultItem = document.createElement('div');
            resultItem.className = 'result-item';
            resultItem.dataset.lat = String(result.latitude);
            resultItem.dataset.lng = String(result.longitude);
            resultItem.textContent = result.display_name; // Safe - no XSS
            
            // Add click handler
            resultItem.addEventListener('click', () => {
                const lat = parseFloat(resultItem.dataset.lat || '0');
                const lng = parseFloat(resultItem.dataset.lng || '0');
                this.placeMarker(L.latLng(lat, lng));
                this.map.setView([lat, lng], 15);
                resultsDiv.classList.remove('has-results');
            });
            
            resultsDiv.appendChild(resultItem);
        });
        
        resultsDiv.classList.add('has-results');
    }
    
    setupSearchBox(): void {
        const searchInput = document.getElementById('geocode-search') as HTMLInputElement;
        if (!searchInput) return;
        
        searchInput.addEventListener('input', (e) => {
            const query = (e.target as HTMLInputElement).value;
            
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
            }
            
            this.searchTimeout = window.setTimeout(() => {
                this.searchLocation(query);
            }, 300);
        });
    }
    
    onRadiusChange(callback: (radius: number) => void): void {
        const radiusSlider = document.getElementById('radius-slider') as HTMLInputElement;
        const radiusValue = document.getElementById('radius-value');
        
        if (!radiusSlider) return;
        
        radiusSlider.addEventListener('input', () => {
            const radius = parseInt(radiusSlider.value);
            if (radiusValue) {
                radiusValue.textContent = `${radius}m`;
            }
            
            // Update circle if marker exists
            if (this.marker) {
                this.updateCircle(this.marker.getLatLng());
            }
            
            callback(radius);
        });
    }
    
    getSelectedLocation(): { lat: number; lng: number; name: string } | null {
        if (!this.marker) return null;
        
        const latlng = this.marker.getLatLng();
        const locationInput = document.getElementById('location-name') as HTMLInputElement;
        
        return {
            lat: latlng.lat,
            lng: latlng.lng,
            name: locationInput?.value || ''
        };
    }
}
